import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  FiChevronDown,
  FiChevronUp,
  FiAlertCircle,
  FiAlertTriangle,
  FiDownload,
  FiClock,
  FiUsers,
} from 'react-icons/fi';
import { dashboardApi } from '../../api';
import { Loader } from '../Common/Loader';
import { showToast } from '../../utils/toast';
import type { PayoutDay, PayoutRangeType, PayoutReport, PayoutReportParams } from '../../types';

const TZ = 'America/New_York';
const LAST_DAYS_PRESETS = [10, 15, 20, 30];
const PERIOD_OPTION_COUNT = 12;
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** Today's date in Eastern time as YYYY-MM-DD (pay periods and cutoffs are ET). */
const todayEtKey = (): string =>
  new Intl.DateTimeFormat('en-CA', { timeZone: TZ, year: 'numeric', month: '2-digit', day: '2-digit' }).format(
    new Date()
  );

const pad2 = (n: number) => String(n).padStart(2, '0');
const lastDayOfMonth = (year: number, month: number) => new Date(Date.UTC(year, month, 0)).getUTCDate();

/**
 * Pay periods: 1 = 1st–15th, 2 = 16th–month end. Newest MONTH first, but within
 * each month the 1st-half is listed before the 2nd-half (calendar order) — e.g.
 * Oct 1–15, Oct 16–31 (current), Sep 1–15, Sep 16–30, ...
 */
const buildPeriodOptions = (): { value: string; label: string; startDate: string; endDate: string }[] => {
  const [y, m, d] = todayEtKey().split('-').map(Number);
  const currentHalf = d <= 15 ? 1 : 2;
  const options: { value: string; label: string; startDate: string; endDate: string }[] = [];

  let year = y;
  let month = m;
  let isCurrentMonth = true;

  while (options.length < PERIOD_OPTION_COUNT) {
    // The current month only has a 2nd half once we're past the 15th — don't
    // list a future, not-yet-started period.
    const halves = isCurrentMonth && currentHalf === 1 ? [1] : [1, 2];
    for (const half of halves) {
      const endDay = half === 1 ? 15 : lastDayOfMonth(year, month);
      const startDay = half === 1 ? 1 : 16;
      const isCurrent = isCurrentMonth && half === currentHalf;
      options.push({
        value: `${year}-${pad2(month)}-${half}`,
        label: `${MONTHS[month - 1]} ${startDay} – ${endDay}, ${year}${isCurrent ? ' (current)' : ''}`,
        startDate: `${year}-${pad2(month)}-${pad2(startDay)}`,
        endDate: `${year}-${pad2(month)}-${pad2(endDay)}`,
      });
    }

    isCurrentMonth = false;
    month -= 1;
    if (month === 0) {
      month = 12;
      year -= 1;
    }
  }

  return options.slice(0, PERIOD_OPTION_COUNT);
};

/** Date keys are calendar days — format them without shifting through the browser timezone. */
const formatDateKey = (key: string, withWeekday = true): string =>
  new Date(`${key}T12:00:00Z`).toLocaleDateString('en-US', {
    timeZone: 'UTC',
    ...(withWeekday ? { weekday: 'short' } : {}),
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });

const money = (n: number): string =>
  `$${(Number(n) || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

/** Day rates can have more than 2 decimals (e.g. $33/mo ÷ 20 = $1.65). */
const rateText = (n: number): string => {
  const fixed = (Number(n) || 0).toFixed(4).replace(/0{1,2}$/, '');
  return `$${fixed}`;
};

const CAPTURE_BADGES: Record<PayoutDay['captureType'], { label: string; className: string; title: string } | null> = {
  scheduled: null,
  late: {
    label: 'Late capture',
    className: 'bg-amber-500/15 text-amber-500 border-amber-500/30',
    title: 'Server missed the 12:00 PM ET run; followers recorded when it came back up.',
  },
  manual: {
    label: 'Manual capture',
    className: 'bg-amber-500/15 text-amber-500 border-amber-500/30',
    title: 'Captured by an admin after 12:00 PM ET because the scheduled run was missing.',
  },
  legacy: {
    label: 'Pre-noon rule',
    className: 'bg-slate-500/15 text-slate-400 border-slate-500/30',
    title: 'Old snapshot taken at midnight ET, before the 12:00 PM cutoff rule.',
  },
};

const csvEscape = (field: unknown) => `"${String(field ?? '').replace(/"/g, '""')}"`;

/** Master's own payout: monthly rate ÷ 20, capped at 20 paid days per calendar month. */
export const MasterPayoutReport: React.FC = () => {
  const periodOptions = useMemo(buildPeriodOptions, []);
  // Default selection must be the actual current period, not list position —
  // the list is now ordered calendar-wise within each month (1–15 before
  // 16–end), so the current period isn't always the first entry.
  const currentPeriodOption = useMemo(
    () => periodOptions.find((p) => p.label.endsWith('(current)')) || periodOptions[0],
    [periodOptions]
  );

  const [rangeType, setRangeType] = useState<PayoutRangeType>('period');
  const [period, setPeriod] = useState<string>(currentPeriodOption.value);
  const [lastDays, setLastDays] = useState<string>('15');
  const [startDate, setStartDate] = useState<string>(currentPeriodOption.startDate);
  const [endDate, setEndDate] = useState<string>(todayEtKey());
  const [includeWeekends, setIncludeWeekends] = useState<boolean>(false);

  // Manual, per-session only — never sent to the server or saved anywhere.
  const [dailyFeeRate, setDailyFeeRate] = useState<number | string>(0.5);
  const effectiveDailyFeeRate = Math.max(0, Number(dailyFeeRate) || 0);

  const [report, setReport] = useState<PayoutReport | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [detailView, setDetailView] = useState<'followers' | 'days'>('followers');
  const [expandedDays, setExpandedDays] = useState<Set<string>>(new Set());

  const params = useMemo((): Omit<PayoutReportParams, 'masterId'> | null => {
    const base: Omit<PayoutReportParams, 'masterId'> = { rangeType, includeWeekends };
    if (rangeType === 'period') base.period = period;
    if (rangeType === 'lastDays') {
      const n = parseInt(lastDays, 10);
      if (!Number.isFinite(n) || n < 1 || n > 366) return null;
      base.lastDays = n;
    }
    if (rangeType === 'custom') {
      if (!startDate || !endDate || startDate > endDate) return null;
      base.startDate = startDate;
      base.endDate = endDate;
    }
    return base;
  }, [rangeType, period, lastDays, startDate, endDate, includeWeekends]);

  const fetchReport = useCallback(async () => {
    if (!params) return;
    try {
      setIsLoading(true);
      const res = await dashboardApi.getMyMasterPayoutReport(params);
      if (res.success) {
        setReport(res);
        setExpandedDays(new Set());
      }
    } catch (err: any) {
      showToast('error', err?.response?.data?.error || 'Failed to load payout report');
    } finally {
      setIsLoading(false);
    }
  }, [params]);

  useEffect(() => {
    const t = setTimeout(fetchReport, 350);
    return () => clearTimeout(t);
  }, [fetchReport]);

  const handleDownloadCSV = () => {
    if (!report || !singleMaster) {
      showToast('error', 'No report data available to export.');
      return;
    }
    const { range } = report;
    const m = singleMaster;
    const rows: unknown[][] = [
      ['Your Payout Report'],
      ['Range (ET)', range.startDate, 'to', range.endDate],
      ['Weekends', range.includeWeekends ? 'Included' : 'Excluded'],
      [
        'Credit rule',
        `Following at ${range.creditCutoff} gets that day; day rate entered manually below; max ${range.paidDaysPerMonth} paid days per calendar month`,
      ],
      ['Daily Fee Price ($)', 'Manually entered — not saved', effectiveDailyFeeRate.toFixed(2)],
      [],
      ['TOTAL'],
      ['Daily Fee Price ($)', 'Follower-Days Paid', 'Amount ($)', 'Days With Data', 'Days Over The Monthly Cap'],
      [effectiveDailyFeeRate.toFixed(2), m.followerDays, m.amount.toFixed(2), m.daysWithData, m.cappedDays],
      [],
      ['PER FOLLOWER'],
      ['Follower', 'Follower Email', 'Days Paid', 'Amount ($)', 'Dates Paid', 'Days Over Cap'],
      ...m.followers.map((f) => [f.name, f.email, f.days, f.amount.toFixed(2), f.dates.join(' '), f.cappedDays]),
      [],
      ['DAILY DETAIL'],
      ['Date', 'Weekday', 'Follower', 'Follower Email', 'Daily Fee Price ($)', 'Paid?', 'Capture'],
      ...[...m.days].reverse().flatMap((d) => {
        const weekday = formatDateKey(d.dateKey).split(',')[0];
        const capture = d.captureType === 'legacy' ? 'pre-noon rule (midnight)' : d.captureType;
        if (d.followers.length === 0) {
          return [[d.dateKey, weekday, 'No followers', '', d.dailyRate, '', capture]];
        }
        return d.followers.map((f) => [
          d.dateKey,
          weekday,
          f.name,
          f.email,
          d.dailyRate,
          f.billable ? 'Yes' : 'No — over monthly cap',
          capture,
        ]);
      }),
    ];

    const csvContent = rows.map((row) => row.map(csvEscape).join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `My_Payout_${range.startDate}_to_${range.endDate}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    showToast('success', 'Payout report exported to CSV');
  };

  const toggleDay = (key: string) => {
    setExpandedDays((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const rawMaster = report?.masters[0] || null;

  /** Overrides the server-stored rate with the manually-entered one for every calculation below. */
  const singleMaster = useMemo(() => {
    if (!rawMaster) return null;
    const days = rawMaster.days.map((d) => ({
      ...d,
      dailyRate: effectiveDailyFeeRate,
      amount: d.followerCount * effectiveDailyFeeRate,
    }));
    const followers = rawMaster.followers.map((f) => ({
      ...f,
      amount: f.days * effectiveDailyFeeRate,
    }));
    return {
      ...rawMaster,
      currentDailyRate: effectiveDailyFeeRate,
      amount: rawMaster.followerDays * effectiveDailyFeeRate,
      days,
      followers,
    };
  }, [rawMaster, effectiveDailyFeeRate]);

  const rangeTabs: { key: PayoutRangeType; label: string }[] = [
    { key: 'period', label: 'Pay period' },
    { key: 'lastDays', label: 'Last N days' },
    { key: 'custom', label: 'Custom' },
  ];

  // `tabular-nums` in index.css also switches to a mono font — use the property alone to keep Inter
  const nums = '[font-variant-numeric:tabular-nums]';
  const inputClass =
    'h-10 px-3 bg-background border border-border rounded-lg text-sm text-foreground focus:outline-none focus:border-primary/60 focus:ring-2 focus:ring-primary/20 transition';
  const tabClass = (active: boolean) =>
    `px-3 h-8 rounded-md text-xs font-medium transition-colors whitespace-nowrap ${
      active ? 'bg-surface text-foreground shadow-sm' : 'text-muted hover:text-foreground'
    }`;
  const chipClass = (active: boolean) =>
    `h-10 px-3 rounded-lg border text-sm transition-colors ${
      active ? 'border-primary/50 text-primary bg-primary/10' : 'border-border text-muted hover:text-foreground'
    }`;

  /** "Oct 16, 19, 20 · Nov 2" — compact list of date keys */
  const shortDates = (keys: string[]) => {
    const groups: { month: string; days: number[] }[] = [];
    [...keys].sort().forEach((k) => {
      const [, m, d] = k.split('-').map(Number);
      const month = MONTHS[m - 1];
      const last = groups[groups.length - 1];
      if (last && last.month === month) last.days.push(d);
      else groups.push({ month, days: [d] });
    });
    return groups.map((g) => `${g.month} ${g.days.join(', ')}`).join(' · ');
  };

  return (
    <div className="space-y-4 max-w-6xl mx-auto pb-12">
      {/* Header */}
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h2 className="text-lg font-semibold text-foreground">Payout report</h2>
          {report ? (
            <p className={`text-sm text-muted mt-0.5 ${nums}`}>
              {formatDateKey(report.range.startDate, false)} – {formatDateKey(report.range.endDate, false)} ·{' '}
              {report.range.countedDaysInRange} {report.range.includeWeekends ? 'days' : 'weekdays'}
            </p>
          ) : (
            <p className="text-sm text-muted mt-0.5">Daily fee per follower, counted at 12:00 ET</p>
          )}
        </div>
        <button
          type="button"
          onClick={handleDownloadCSV}
          disabled={!report || !singleMaster || isLoading}
          className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg border border-border text-sm text-muted hover:text-foreground hover:border-primary/40 transition-colors disabled:opacity-40 disabled:cursor-not-allowed shrink-0"
        >
          <FiDownload className="h-4 w-4" />
          <span className="hidden sm:inline">Export</span>
        </button>
      </div>

      {/* Filters */}
      <div className="bg-surface border border-border rounded-xl p-3 sm:p-4">
        <div className="flex flex-col lg:flex-row lg:items-center gap-3">
          <div className="inline-flex p-1 rounded-lg bg-background border border-border self-start lg:self-auto">
            {rangeTabs.map((tab) => (
              <button
                key={tab.key}
                type="button"
                onClick={() => setRangeType(tab.key)}
                className={tabClass(rangeType === tab.key)}
              >
                {tab.label}
              </button>
            ))}
          </div>

          <div className="flex-1 min-w-0">
            {rangeType === 'period' && (
              <select
                value={period}
                onChange={(e) => setPeriod(e.target.value)}
                className={`${inputClass} w-full cursor-pointer`}
                aria-label="Pay period"
              >
                {periodOptions.map((p) => (
                  <option key={p.value} value={p.value}>
                    {p.label}
                  </option>
                ))}
              </select>
            )}

            {rangeType === 'lastDays' && (
              <div className="flex items-center gap-2">
                {LAST_DAYS_PRESETS.map((n) => (
                  <button
                    key={n}
                    type="button"
                    onClick={() => setLastDays(String(n))}
                    className={chipClass(lastDays === String(n))}
                  >
                    {n}
                  </button>
                ))}
                <input
                  type="number"
                  min={1}
                  max={366}
                  value={lastDays}
                  onChange={(e) => setLastDays(e.target.value)}
                  className={`${inputClass} w-20`}
                  aria-label={`Number of ${includeWeekends ? 'days' : 'weekdays'}`}
                />
              </div>
            )}

            {rangeType === 'custom' && (
              <div className="flex items-center gap-2">
                <input
                  type="date"
                  value={startDate}
                  max={endDate || undefined}
                  onChange={(e) => setStartDate(e.target.value)}
                  className={`${inputClass} flex-1 min-w-0 cursor-pointer`}
                  aria-label="Start date"
                />
                <span className="text-muted text-sm">–</span>
                <input
                  type="date"
                  value={endDate}
                  min={startDate || undefined}
                  onChange={(e) => setEndDate(e.target.value)}
                  className={`${inputClass} flex-1 min-w-0 cursor-pointer`}
                  aria-label="End date"
                />
              </div>
            )}
          </div>

          <div className="flex items-center gap-3">
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-muted pointer-events-none">$</span>
              <input
                type="number"
                min={0}
                step="0.01"
                value={dailyFeeRate}
                onChange={(e) => setDailyFeeRate(e.target.value)}
                className={`${inputClass} w-32 pl-6 pr-11`}
                aria-label="Daily fee per follower"
                title="Daily fee per follower, per day"
              />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-muted pointer-events-none">/day</span>
            </div>

            <label className="flex items-center gap-2 cursor-pointer select-none whitespace-nowrap">
              <button
                type="button"
                role="switch"
                aria-checked={includeWeekends}
                onClick={() => setIncludeWeekends(!includeWeekends)}
                className={`relative inline-flex h-5 w-9 shrink-0 rounded-full transition-colors ${
                  includeWeekends ? 'bg-primary' : 'bg-border'
                }`}
              >
                <span
                  className={`absolute top-0.5 left-0.5 h-4 w-4 rounded-full bg-white shadow transition-transform ${
                    includeWeekends ? 'translate-x-4' : 'translate-x-0'
                  }`}
                />
              </button>
              <span className="text-sm text-muted">Weekends</span>
            </label>
          </div>
        </div>

        {!params && (
          <p className="mt-2 text-xs text-warning flex items-center gap-1.5">
            <FiAlertCircle className="h-3.5 w-3.5" />
            {rangeType === 'lastDays' ? 'Enter a number of days between 1 and 366.' : 'Start date must be on or before end date.'}
          </p>
        )}
      </div>

      {/* Results */}
      {isLoading && !report ? (
        <div className="py-16 text-center bg-surface border border-border rounded-xl">
          <Loader message="Calculating your payout..." />
        </div>
      ) : !report || !singleMaster ? null : (
        <div className={`space-y-4 transition-opacity ${isLoading ? 'opacity-60' : ''}`}>
          {/* Summary */}
          <div className="bg-surface border border-border rounded-xl p-5 flex flex-col md:flex-row md:items-center justify-between gap-5">
            <div className="min-w-0">
              <p className="text-xs text-muted">Your earnings</p>
              <p className={`mt-1 text-3xl font-semibold text-success ${nums}`}>{money(singleMaster.amount)}</p>
              <p className={`mt-1 text-xs text-muted ${nums}`}>
                {singleMaster.followerDays} follower-day{singleMaster.followerDays === 1 ? '' : 's'} ×{' '}
                {rateText(singleMaster.currentDailyRate)}
              </p>
            </div>

            <dl className={`grid grid-cols-3 md:flex md:items-center md:divide-x divide-border text-sm ${nums}`}>
              <Stat label="Followers" value={singleMaster.followers.length} />
              <Stat label="Days with data" value={`${singleMaster.daysWithData}/${report.range.countedDaysInRange}`} />
              <Stat
                label="Over cap"
                value={singleMaster.cappedDays}
                className={singleMaster.cappedDays > 0 ? 'text-warning' : undefined}
              />
            </dl>
          </div>

          {/* Notices */}
          {(singleMaster.missingDays.length > 0 ||
            singleMaster.cappedDays > 0 ||
            singleMaster.hasLegacyDays ||
            report.range.endDate > report.range.lastCapturableDate) && (
            <ul className="space-y-1.5 text-xs">
              {singleMaster.missingDays.length > 0 && (
                <li className="flex gap-2 text-danger">
                  <FiAlertTriangle className="h-3.5 w-3.5 mt-px shrink-0" />
                  <span>
                    No snapshot (not paid): <span className={nums}>{shortDates(singleMaster.missingDays)}</span>
                  </span>
                </li>
              )}
              {singleMaster.cappedDays > 0 && (
                <li className="flex gap-2 text-warning">
                  <FiAlertCircle className="h-3.5 w-3.5 mt-px shrink-0" />
                  {singleMaster.cappedDays} follower-day{singleMaster.cappedDays === 1 ? '' : 's'} over the{' '}
                  {report.range.paidDaysPerPeriod}-day pay-period cap (not paid)
                </li>
              )}
              {report.range.endDate > report.range.lastCapturableDate && (
                <li className="flex gap-2 text-muted">
                  <FiClock className="h-3.5 w-3.5 mt-px shrink-0" />
                  Days after {formatDateKey(report.range.lastCapturableDate, false)} aren't captured yet
                </li>
              )}
              {singleMaster.hasLegacyDays && (
                <li className="flex gap-2 text-muted">
                  <FiClock className="h-3.5 w-3.5 mt-px shrink-0" />
                  Some days use old midnight snapshots (marked “Pre-noon rule”)
                </li>
              )}
            </ul>
          )}

          {/* Breakdown */}
          <div className="bg-surface border border-border rounded-xl overflow-hidden">
            <div className="flex items-center gap-5 px-5 border-b border-border">
              {(
                [
                  { key: 'followers', label: 'By follower', count: singleMaster.followers.length },
                  { key: 'days', label: 'By day', count: singleMaster.daysWithData },
                ] as const
              ).map((t) => (
                <button
                  key={t.key}
                  type="button"
                  onClick={() => setDetailView(t.key)}
                  className={`-mb-px py-3 text-sm border-b-2 transition-colors ${
                    detailView === t.key
                      ? 'border-primary text-foreground font-medium'
                      : 'border-transparent text-muted hover:text-foreground'
                  }`}
                >
                  {t.label} <span className="text-muted">{t.count}</span>
                </button>
              ))}
            </div>

            {detailView === 'followers' ? (
              singleMaster.followers.length === 0 ? (
                <EmptyState />
              ) : (
                <div className="table-scroll overflow-x-auto">
                  <table className={`w-full min-w-[480px] text-sm ${nums}`}>
                    <thead>
                      <tr className="text-left text-xs text-muted border-b border-border">
                        <th className="py-2.5 px-5 font-medium">Follower</th>
                        <th className="py-2.5 px-5 font-medium text-right">Days paid</th>
                        <th className="py-2.5 px-5 font-medium text-right">Amount</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                      {singleMaster.followers.map((f) => (
                        <tr key={f._id} className="hover:bg-background/40">
                          <td className="py-3 px-5">
                            <div className="text-foreground">{f.name}</div>
                            <div className="text-xs text-muted">{f.email}</div>
                          </td>
                          <td className="py-3 px-5 text-right text-foreground" title={f.dates.join(', ')}>
                            {f.days}
                            {f.cappedDays > 0 && (
                              <span
                                className="ml-1.5 text-xs text-warning"
                                title={`${f.cappedDays} day(s) over the ${report.range.paidDaysPerPeriod}-day pay-period cap, not paid`}
                              >
                                +{f.cappedDays} capped
                              </span>
                            )}
                          </td>
                          <td className="py-3 px-5 text-right font-medium text-success">{money(f.amount)}</td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr className="border-t border-border">
                        <td className="py-3 px-5 text-xs text-muted">Total</td>
                        <td className="py-3 px-5 text-right text-foreground">{singleMaster.followerDays}</td>
                        <td className="py-3 px-5 text-right font-semibold text-success">{money(singleMaster.amount)}</td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              )
            ) : singleMaster.days.length === 0 ? (
              <EmptyState />
            ) : (
              <div className={`divide-y divide-border text-sm ${nums}`}>
                {singleMaster.days.map((d) => {
                  const isExpanded = expandedDays.has(d.dateKey);
                  const badge = CAPTURE_BADGES[d.captureType];
                  return (
                    <div key={d.dateKey}>
                      <button
                        type="button"
                        onClick={() => toggleDay(d.dateKey)}
                        aria-expanded={isExpanded}
                        className="w-full flex items-center justify-between gap-3 px-5 py-3 text-left hover:bg-background/40 transition-colors"
                      >
                        <span className="flex items-center gap-2 min-w-0 flex-wrap">
                          <span className="text-foreground">{formatDateKey(d.dateKey)}</span>
                          {d.isWeekend && <span className="text-xs text-muted">· Weekend</span>}
                          {badge && (
                            <span title={badge.title} className="text-xs text-warning">
                              · {badge.label}
                            </span>
                          )}
                        </span>
                        <span className="flex items-center gap-4 shrink-0">
                          <span className="text-xs text-muted">
                            {d.followerCount}
                            {d.totalFollowerCount > d.followerCount && (
                              <span className="text-warning">/{d.totalFollowerCount}</span>
                            )}{' '}
                            × {rateText(d.dailyRate)}
                          </span>
                          <span className="w-20 text-right font-medium text-success">{money(d.amount)}</span>
                          {isExpanded ? (
                            <FiChevronUp className="h-4 w-4 text-muted" />
                          ) : (
                            <FiChevronDown className="h-4 w-4 text-muted" />
                          )}
                        </span>
                      </button>
                      {isExpanded && (
                        <div className="px-5 pb-3">
                          {d.followers.length === 0 ? (
                            <p className="text-xs text-muted">No followers at the snapshot.</p>
                          ) : (
                            <ul className="space-y-1 text-xs">
                              {d.followers.map((f) => (
                                <li key={f._id} className={`flex items-center gap-2 ${f.billable ? 'text-foreground' : 'text-muted'}`}>
                                  <span className="h-1 w-1 rounded-full bg-border shrink-0" />
                                  <span className="truncate">
                                    {f.name}
                                    {f.email && <span className="text-muted"> · {f.email}</span>}
                                  </span>
                                  {!f.billable && (
                                    <span
                                      className="text-warning shrink-0"
                                      title={`Already at ${report.range.paidDaysPerMonth} paid days this month`}
                                    >
                                      capped
                                    </span>
                                  )}
                                </li>
                              ))}
                            </ul>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const Stat: React.FC<{ label: string; value: React.ReactNode; className?: string }> = ({ label, value, className }) => (
  <div className="md:px-6 first:md:pl-0 last:md:pr-0">
    <dt className="text-xs text-muted">{label}</dt>
    <dd className={`mt-0.5 text-lg font-semibold ${className || 'text-foreground'}`}>{value}</dd>
  </div>
);

const EmptyState: React.FC = () => (
  <div className="py-12 text-center">
    <FiUsers className="h-6 w-6 text-muted mx-auto mb-2" />
    <p className="text-sm text-foreground">No follower data</p>
    <p className="text-xs text-muted mt-0.5">No snapshots match the selected filters.</p>
  </div>
);

export default MasterPayoutReport;
